import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";
import { DeleteConfirm } from "./AdminServices";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

const TeamModal = ({ member, onClose, onSaved }) => {
  const { toast, show } = useToast();
  const isEdit = !!member?.id;

  const [form, setForm] = useState({
    name:     member?.name     || "",
    role:     member?.role     || "",
    bio:      member?.bio      || "",
    image:    member?.image    || "",
    linkedin: member?.linkedin || "",
    github:   member?.github   || "",
    twitter:  member?.twitter  || "",
    skills:   member?.skills?.join(", ") || "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { show("Name is required", "error"); return; }
    setSaving(true);

    // Convert skills comma string to array
    const payload = {
      ...form,
      skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
    };

    try {
      if (isEdit) {
        await authFetch(`${ENDPOINTS.team_members}/${member.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await authFetch(ENDPOINTS.team_members, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      show(isEdit ? "Member updated!" : "Member added!");
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="adm-modal-backdrop" onClick={onClose}>
        <div className="adm-modal" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
          <div className="adm-modal-header">
            <h3 className="adm-modal-title">{isEdit ? "Edit Team Member" : "Add Team Member"}</h3>
            <button className="adm-modal-close" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="adm-modal-body">
              <div className="adm-form">

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Full Name *</label>
                    <input name="name" className="adm-form-input" value={form.name}
                      onChange={handleChange} placeholder="Jane Mwangi" required />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Role / Title</label>
                    <input name="role" className="adm-form-input" value={form.role}
                      onChange={handleChange} placeholder="UI/UX Designer" />
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Bio</label>
                  <textarea name="bio" className="adm-form-textarea" value={form.bio}
                    onChange={handleChange} placeholder="Short bio about this team member" />
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Profile Image URL</label>
                  <input name="image" className="adm-form-input" value={form.image}
                    onChange={handleChange} placeholder="/images/team/jane.jpg or https://..." />
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">
                    Skills <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>(comma separated)</span>
                  </label>
                  <input name="skills" className="adm-form-input" value={form.skills}
                    onChange={handleChange} placeholder="React, Figma, UI/UX, Python" />
                </div>

                <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem" }}>
                  <p className="adm-form-label" style={{ marginBottom: "0.75rem" }}>Social Links</p>
                  <div className="adm-form">
                    <div className="adm-form-field">
                      <div className="adm-input-wrap" style={{ position: "relative" }}>
                        <i className="fa-brands fa-linkedin" style={{ position: "absolute", left: "0.875rem", top: "50%", transform: "translateY(-50%)", color: "#0a77b6", fontSize: "0.875rem" }}></i>
                        <input name="linkedin" className="adm-form-input" value={form.linkedin}
                          onChange={handleChange} placeholder="https://linkedin.com/in/..." style={{ paddingLeft: "2.5rem" }} />
                      </div>
                    </div>
                    <div className="adm-form-field">
                      <div style={{ position: "relative" }}>
                        <i className="fa-brands fa-github" style={{ position: "absolute", left: "0.875rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.875rem" }}></i>
                        <input name="github" className="adm-form-input" value={form.github}
                          onChange={handleChange} placeholder="https://github.com/..." style={{ paddingLeft: "2.5rem" }} />
                      </div>
                    </div>
                    <div className="adm-form-field">
                      <div style={{ position: "relative" }}>
                        <i className="fa-brands fa-x-twitter" style={{ position: "absolute", left: "0.875rem", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", fontSize: "0.875rem" }}></i>
                        <input name="twitter" className="adm-form-input" value={form.twitter}
                          onChange={handleChange} placeholder="https://twitter.com/..." style={{ paddingLeft: "2.5rem" }} />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="adm-save-btn" disabled={saving}>
                {saving ? "Saving..." : <><i className="fa-solid fa-check"></i> {isEdit ? "Update" : "Add Member"}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

const AdminTeam = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const { toast, show }       = useToast();

  const load = useCallback(async () => {
    try {
      const data = await authFetch(ENDPOINTS.team_members);
      setMembers(data);
    } catch (err) {
      show(err.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Team Members</h1>
          <p className="adm-page-sub">{members.length} members</p>
        </div>
        <button className="adm-add-btn" onClick={() => setModal({ type: "add" })}>
          <i className="fa-solid fa-user-plus"></i> Add Member
        </button>
      </div>

      <div className="adm-table-wrap">
        {loading ? (
          <div className="adm-loading"><div className="adm-loading-spinner"></div></div>
        ) : members.length === 0 ? (
          <div className="adm-empty">No team members yet.</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr><th>Member</th><th>Role</th><th>Skills</th><th>Social</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                        background: "linear-gradient(135deg,#0a6b5a,#074a3f)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "'Syne',sans-serif", fontWeight: 700, color: "white", fontSize: "0.95rem",
                      }}>
                        {m.name?.[0]?.toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                    </div>
                  </td>
                  <td style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>{m.role || "—"}</td>
                  <td style={{ fontSize: "0.8rem" }}>
                    <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                      {m.skills?.slice(0, 3).map((s) => (
                        <span key={s} className="adm-badge adm-badge--green">{s}</span>
                      ))}
                      {m.skills?.length > 3 && (
                        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem" }}>+{m.skills.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      {m.linkedin && <a href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: "#0a77b6", fontSize: "0.9rem" }}><i className="fa-brands fa-linkedin"></i></a>}
                      {m.github && <a href={m.github} target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}><i className="fa-brands fa-github"></i></a>}
                      {m.twitter && <a href={m.twitter} target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}><i className="fa-brands fa-x-twitter"></i></a>}
                    </div>
                  </td>
                  <td>
                    <div className="adm-action-btns">
                      <button className="adm-edit-btn" onClick={() => setModal({ type: "edit", item: m })}>
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button className="adm-delete-btn" onClick={() => setModal({ type: "delete", item: m })}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal?.type === "add" && <TeamModal member={null} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "edit" && <TeamModal member={modal.item} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "delete" && (
        <DeleteConfirm item={modal.item} label="Team Member" endpoint={ENDPOINTS.team_members}
          onClose={() => setModal(null)} onDeleted={() => { load(); show("Member removed."); }} />
      )}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminTeam;
